<template>
  <header
    ref="topbarRoot"
    class="admin-topbar admin-topbar--responsive d-flex align-items-center gap-2 px-3 px-lg-4"
    :class="{
      'admin-topbar-unified-filters': useUnifiedFilterBar,
      'admin-topbar-with-context-filter': showSelectFilter && !showDateFilters,
      'admin-topbar-with-date-filters': showDateFilters && !showSelectFilter && !showDashboardGeoFilters && !useSplitFilterBar,
      'admin-topbar-with-leading-date-filters': showDateFiltersInLeading,
      'admin-topbar-with-full-filters': showDateFilters && showSelectFilter && !useSplitFilterBar,
      'admin-topbar-with-dashboard-geo': showDashboardGeoFilters && showDateFilters,
      'admin-topbar-with-split-filters': useSplitFilterBar,
      'admin-topbar-with-search-category': showSearch && showSearchCategory,
      'admin-topbar--dark': isDarkMode
    }"
  >
    <button
      type="button"
      class="admin-topbar-icon admin-topbar-menu-btn d-md-none order-0 flex-shrink-0"
      aria-label="Open menu"
      @click="$emit('toggle-sidebar')"
    >
      <font-awesome-icon :icon="['fas', 'bars']" />
    </button>

    <div
      v-if="!hasLeadingContent"
      class="admin-topbar-spacer order-1"
      aria-hidden="true"
    ></div>

    <div
      v-if="hasLeadingContent"
      class="admin-topbar-leading order-1 order-md-1"
      :class="{ 'admin-topbar-leading-unified': useUnifiedFilterBar }"
    >
      <div v-if="showDashboardGeoFilters" class="topbar-dashboard-geo-filters">
        <div
          v-show="!isOverflowed('dashboard-primary')"
          class="topbar-overflow-item topbar-dashboard-geo-item"
          data-overflow-id="dashboard-primary"
        >
          <select
            class="form-select topbar-dashboard-select dark-filter-control"
            :value="dashboardPrimarySelected"
            @change="$emit('update:dashboardPrimarySelected', $event.target.value)"
          >
            <option v-for="opt in dashboardPrimaryOptions" :key="`primary-${String(opt.value)}`" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </div>
        <div
          v-show="!isOverflowed('dashboard-secondary')"
          class="topbar-overflow-item topbar-dashboard-geo-item"
          data-overflow-id="dashboard-secondary"
        >
          <select
            class="form-select topbar-dashboard-select dark-filter-control"
            :value="dashboardSecondarySelected"
            @change="$emit('update:dashboardSecondarySelected', $event.target.value)"
          >
            <option v-for="opt in dashboardSecondaryOptions" :key="`secondary-${String(opt.value)}`" :value="opt.value">
              {{ opt.text }}
            </option>
          </select>
        </div>
      </div>
      <div
        v-else-if="showSearch"
        class="topbar-search-combo"
        :class="{ 'topbar-search-combo--with-category': showSearchCategory || showSelectFilterInSearchCombo }"
      >
        <div class="admin-topbar-search admin-topbar-search--pinned">
          <span class="topbar-search-icon" aria-hidden="true">
            <img :src="searchIcon" alt="" class="topbar-search-icon-img" />
          </span>
          <input
            :value="search"
            type="search"
            class="form-control"
            :placeholder="searchPlaceholder"
            @input="$emit('update:search', $event.target.value)"
            @keydown.enter="$emit('apply-filter')"
          />
        </div>
        <div
          v-if="showSearchCategory"
          v-show="!isOverflowed('search-category')"
          class="topbar-context-filter topbar-search-category-filter topbar-overflow-item"
          data-overflow-id="search-category"
          data-v-step="0"
        >
          <b-dropdown
            variant="link"
            class="topbar-select-filter"
            toggle-class="topbar-select-filter-toggle dark-filter-control"
            menu-class="topbar-select-filter-menu dark-filter-menu"
            boundary="viewport"
            no-caret
          >
            <template #button-content>
              <span class="topbar-select-filter-text">{{ searchCategorySelected || searchCategoryFallbackLabel }}</span>
              <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
            </template>
            <b-dropdown-item
              v-for="option in availableSearchCategoryOptions"
              :key="option"
              :link-class="[
                'topbar-select-filter-item',
                { 'topbar-select-filter-item--active': option === (searchCategorySelected || searchCategoryFallbackLabel) }
              ]"
              @click="$emit('update:searchCategorySelected', option)"
            >
              {{ option }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <div
          v-else-if="showSelectFilterInSearchCombo"
          v-show="!isOverflowed('select-filter')"
          class="topbar-context-filter topbar-search-category-filter topbar-overflow-item"
          data-overflow-id="select-filter"
          data-v-step="1"
        >
          <b-dropdown
            variant="link"
            class="topbar-select-filter"
            toggle-class="topbar-select-filter-toggle dark-filter-control"
            menu-class="topbar-select-filter-menu dark-filter-menu"
            boundary="viewport"
            no-caret
          >
            <template #button-content>
              <span class="topbar-select-filter-text">{{ selectedFilterLabel }}</span>
              <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
            </template>
            <b-dropdown-item
              v-for="option in filterOptions"
              :key="`${option.text}-${option.value}`"
              :link-class="[
                'topbar-select-filter-item',
                { 'topbar-select-filter-item--active': option.value === filterSelected }
              ]"
              @click="$emit('update:filterSelected', option.value)"
            >
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <div
        v-else-if="showSearchCategory"
        v-show="!isOverflowed('search-category')"
        class="topbar-context-filter topbar-search-category-filter topbar-overflow-item"
        data-overflow-id="search-category"
        data-v-step="0"
      >
        <b-dropdown
          variant="link"
          class="topbar-select-filter"
          toggle-class="topbar-select-filter-toggle dark-filter-control"
          menu-class="topbar-select-filter-menu dark-filter-menu"
          boundary="viewport"
          no-caret
        >
          <template #button-content>
            <span class="topbar-select-filter-text">{{ searchCategorySelected || searchCategoryFallbackLabel }}</span>
            <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
          </template>
          <b-dropdown-item
            v-for="option in availableSearchCategoryOptions"
            :key="option"
            :link-class="[
              'topbar-select-filter-item',
              { 'topbar-select-filter-item--active': option === (searchCategorySelected || searchCategoryFallbackLabel) }
            ]"
            @click="$emit('update:searchCategorySelected', option)"
          >
            {{ option }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div
        v-if="showSelectFilter && !showSelectFilterInSearchCombo"
        v-show="!isOverflowed('select-filter')"
        class="topbar-context-filter topbar-overflow-item"
        data-overflow-id="select-filter"
        data-v-step="1"
      >
        <b-dropdown
          variant="link"
          class="topbar-select-filter"
          toggle-class="topbar-select-filter-toggle dark-filter-control"
          menu-class="topbar-select-filter-menu dark-filter-menu"
          boundary="viewport"
          no-caret
        >
          <template #button-content>
            <span class="topbar-select-filter-text">{{ selectedFilterLabel }}</span>
            <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
          </template>
          <b-dropdown-item
            v-for="option in filterOptions"
            :key="`${option.text}-${option.value}`"
            link-class="topbar-select-filter-item"
            @click="$emit('update:filterSelected', option.value)"
          >
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <template v-else-if="showDateFiltersInLeading">
        <div
          class="topbar-filter-group topbar-filter-group-leading"
          :class="{ 'topbar-filter-group-reverse-date': reverseDateOrder }"
        >
          <div
            v-show="!isOverflowed('start-date')"
            class="topbar-overflow-item"
            data-overflow-id="start-date"
          >
            <div
              class="topbar-date-field topbar-date-field--start dark-filter-control"
              :class="{ 'topbar-date-field--empty': !startDate, 'topbar-date-field--filled': !!startDate }"
            >
              <span class="topbar-date-text">{{ startDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="startDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:startDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-if="!singleDateFilter"
            v-show="!isOverflowed('end-date')"
            class="topbar-overflow-item"
            data-overflow-id="end-date"
          >
            <div
              class="topbar-date-field topbar-date-field--end dark-filter-control"
              :class="{ 'topbar-date-field--empty': !endDate, 'topbar-date-field--filled': !!endDate }"
            >
              <span class="topbar-date-text">{{ endDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="endDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:endDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-if="showClearButton"
            v-show="!isOverflowed('clear-button')"
            class="topbar-overflow-item"
            data-overflow-id="clear-button"
          >
            <button
              type="button"
              class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
              @click="$emit('clear-dates')"
            >
              Clear
            </button>
          </div>
          <div
            v-show="!isOverflowed('apply-filter')"
            class="topbar-overflow-item"
            data-overflow-id="apply-filter"
          >
            <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
              Apply Filter
            </button>
          </div>
        </div>
      </template>
      <template v-if="useUnifiedFilterBar">
        <span class="topbar-vertical-divider topbar-vertical-divider-leading" aria-hidden="true"></span>
        <div
          class="topbar-filter-group"
          :class="{ 'topbar-filter-group-reverse-date': reverseDateOrder }"
        >
          <div
            class="topbar-date-field topbar-date-field--start dark-filter-control"
            :class="{ 'topbar-date-field--empty': !startDate, 'topbar-date-field--filled': !!startDate }"
          >
            <span class="topbar-date-text">{{ startDateLabel }}</span>
            <b-form-datepicker
              button-only
              :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
              :menu-class="datePickerMenuClass"
              boundary="viewport"
              :popper-opts="datePickerPopperOpts"
              :value="startDate"
              :min="minDate"
              :max="maxDate"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              @input="$emit('update:startDate', $event)"
            >
              <template #button-content>
                <span class="topbar-calendar-trigger">
                  <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                </span>
              </template>
            </b-form-datepicker>
          </div>
          <div
            v-if="!singleDateFilter"
            class="topbar-date-field topbar-date-field--end dark-filter-control"
            :class="{ 'topbar-date-field--empty': !endDate, 'topbar-date-field--filled': !!endDate }"
          >
            <span class="topbar-date-text">{{ endDateLabel }}</span>
            <b-form-datepicker
              button-only
              :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
              :menu-class="datePickerMenuClass"
              boundary="viewport"
              :popper-opts="datePickerPopperOpts"
              :value="endDate"
              :min="minDate"
              :max="maxDate"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              @input="$emit('update:endDate', $event)"
            >
              <template #button-content>
                <span class="topbar-calendar-trigger">
                  <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                </span>
              </template>
            </b-form-datepicker>
          </div>
          <button
            v-if="showClearButton"
            type="button"
            class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
            @click="$emit('clear-dates')"
          >
            Clear
          </button>
          <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
            Apply Filter
          </button>
        </div>
        <span class="topbar-vertical-divider topbar-vertical-divider-actions" aria-hidden="true"></span>
        <div class="topbar-utility-icons topbar-utility-icons-unified">
          <div
            v-show="!isOverflowed('dark-mode')"
            class="topbar-overflow-item"
            data-overflow-id="dark-mode"
          >
            <button
              type="button"
              class="admin-topbar-icon admin-topbar-dark-toggle"
              :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
              @click="$emit('toggle-dark')"
            >
              <img
                :src="isDarkMode ? sunOutlineIcon : moonIcon"
                alt=""
                class="topbar-action-icon topbar-action-icon-theme"
              />
            </button>
          </div>
          <div
            v-if="!isQmsOtpGuestSession && !isQmsPayrollClosureRole"
            v-show="!isOverflowed('help')"
            class="topbar-overflow-item"
            data-overflow-id="help"
          >
            <router-link
              to="/contactUs"
              class="admin-topbar-icon admin-topbar-help-link d-inline-flex"
              title="Help"
            >
              <font-awesome-icon :icon="['far', 'envelope']" class="topbar-action-icon topbar-action-icon-mail" />
              <span class="topbar-help-text">Help</span>
            </router-link>
          </div>
          <span
            v-if="!isQmsOtpGuestSession && !isQmsPayrollClosureRole && !isOverflowed('help')"
            class="topbar-vertical-divider topbar-vertical-divider-utility d-inline-flex"
            aria-hidden="true"
          ></span>
          <div
            v-show="!isOverflowed('notifications')"
            class="topbar-overflow-item"
            data-overflow-id="notifications"
          >
            <button
              type="button"
              class="admin-topbar-icon admin-topbar-icon-bell"
              title="Notifications"
              :aria-expanded="notificationPanelOpen"
              @click="toggleNotificationPanel"
            >
              <font-awesome-icon :icon="['far', 'bell']" class="topbar-action-icon topbar-action-icon-bell" />
              <span v-if="displayNotificationCount" class="topbar-notify-badge">{{ displayNotificationCount }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="!useUnifiedFilterBar"
      class="admin-topbar-actions d-flex align-items-center flex-wrap flex-shrink-0 gap-2 order-2 order-md-3"
      :class="{ 'ms-auto': !showSelectFilter }"
    >
      <template v-if="showMonthYearFilters">
        <div
          v-show="!isOverflowed('month-year')"
          class="topbar-filter-group topbar-month-year-group topbar-overflow-item"
          data-overflow-id="month-year"
        >
          <b-dropdown
            variant="link"
            class="topbar-select-filter topbar-month-filter topbar-search-category-filter"
            toggle-class="topbar-select-filter-toggle dark-filter-control"
            menu-class="topbar-select-filter-menu dark-filter-menu"
            boundary="viewport"
            no-caret
          >
            <template #button-content>
              <span class="topbar-select-filter-text">{{ formatMonthLabel(monthSelected) }}</span>
              <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
            </template>
            <b-dropdown-item
              v-for="option in monthOptions"
              :key="option"
              link-class="topbar-select-filter-item"
              @click="$emit('update:monthSelected', option)"
            >
              {{ formatMonthLabel(option) }}
            </b-dropdown-item>
          </b-dropdown>
          <b-dropdown
            variant="link"
            class="topbar-select-filter topbar-year-filter topbar-search-category-filter"
            toggle-class="topbar-select-filter-toggle dark-filter-control"
            menu-class="topbar-select-filter-menu dark-filter-menu"
            boundary="viewport"
            no-caret
          >
            <template #button-content>
              <span class="topbar-select-filter-text">{{ yearSelected }}</span>
              <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
            </template>
            <b-dropdown-item
              v-for="option in yearOptions"
              :key="option"
              link-class="topbar-select-filter-item"
              @click="$emit('update:yearSelected', option)"
            >
              {{ option }}
            </b-dropdown-item>
          </b-dropdown>
          <button
            v-if="showClearButton"
            type="button"
            class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
            @click="$emit('clear-dates')"
          >
            Clear
          </button>
          <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
            Apply Filter
          </button>
        </div>
        <span class="topbar-vertical-divider topbar-vertical-divider-actions" aria-hidden="true"></span>
      </template>
      <template v-else-if="showDateFilters && !showDateFiltersInLeading">
        <div
          class="topbar-filter-group"
          :class="{ 'topbar-filter-group-reverse-date': reverseDateOrder }"
        >
          <div
            v-show="!isOverflowed('start-date')"
            class="topbar-overflow-item"
            data-overflow-id="start-date"
          >
            <div
              class="topbar-date-field topbar-date-field--start dark-filter-control"
              :class="{ 'topbar-date-field--empty': !startDate, 'topbar-date-field--filled': !!startDate }"
            >
              <span class="topbar-date-text">{{ startDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="startDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:startDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-if="!singleDateFilter"
            v-show="!isOverflowed('end-date')"
            class="topbar-overflow-item"
            data-overflow-id="end-date"
          >
            <div
              class="topbar-date-field topbar-date-field--end dark-filter-control"
              :class="{ 'topbar-date-field--empty': !endDate, 'topbar-date-field--filled': !!endDate }"
            >
              <span class="topbar-date-text">{{ endDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="endDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:endDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-if="showClearButton"
            v-show="!isOverflowed('clear-button')"
            class="topbar-overflow-item"
            data-overflow-id="clear-button"
          >
            <button
              type="button"
              class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
              @click="$emit('clear-dates')"
            >
              Clear
            </button>
          </div>
          <div
            v-show="!isOverflowed('apply-filter')"
            class="topbar-overflow-item"
            data-overflow-id="apply-filter"
          >
            <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
              Apply Filter
            </button>
          </div>
        </div>
        <span class="topbar-vertical-divider topbar-vertical-divider-actions" aria-hidden="true"></span>
      </template>
      <div class="topbar-utility-icons">
        <div
          v-show="!isOverflowed('dark-mode')"
          class="topbar-overflow-item"
          data-overflow-id="dark-mode"
        >
          <button
            type="button"
            class="admin-topbar-icon admin-topbar-dark-toggle"
            :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="$emit('toggle-dark')"
          >
            <img
              :src="isDarkMode ? sunOutlineIcon : moonIcon"
              alt=""
              class="topbar-action-icon topbar-action-icon-theme"
            />
          </button>
        </div>
        <div
          v-if="!isQmsOtpGuestSession && !isQmsPayrollClosureRole"
          v-show="!isOverflowed('help')"
          class="topbar-overflow-item"
          data-overflow-id="help"
        >
          <router-link
            to="/contactUs"
            class="admin-topbar-icon admin-topbar-help-link d-inline-flex"
            title="Help"
          >
            <font-awesome-icon :icon="['far', 'envelope']" class="topbar-action-icon topbar-action-icon-mail" />
            <span class="topbar-help-text">Help</span>
          </router-link>
        </div>
        <span
          v-if="!isQmsOtpGuestSession && !isQmsPayrollClosureRole && !isOverflowed('help')"
          class="topbar-vertical-divider topbar-vertical-divider-utility d-inline-flex"
          aria-hidden="true"
        ></span>
        <div
          v-show="!isOverflowed('notifications')"
          class="topbar-overflow-item"
          data-overflow-id="notifications"
        >
          <button
            type="button"
            class="admin-topbar-icon admin-topbar-icon-bell"
            title="Notifications"
            :aria-expanded="notificationPanelOpen"
            @click="toggleNotificationPanel"
          >
            <font-awesome-icon :icon="['far', 'bell']" class="topbar-action-icon topbar-action-icon-bell" />
            <span v-if="displayNotificationCount" class="topbar-notify-badge">{{ displayNotificationCount }}</span>
          </button>
        </div>
      </div>
    </div>

    <topbar-overflow-menu
      v-if="hasOverflowItems"
      class="admin-topbar-overflow-slot order-3 flex-shrink-0"
      :open="overflowMenuOpen"
      :count="overflowedIds.length"
      :is-dark-mode="isDarkMode"
      @toggle="overflowMenuOpen = !overflowMenuOpen"
      @close="overflowMenuOpen = false"
    >
        <div v-if="isOverflowed('month-year')" class="topbar-overflow-panel-row">
          <span class="topbar-overflow-panel-label">Date filters</span>
          <div class="topbar-filter-group topbar-month-year-group topbar-overflow-panel-row--inline">
            <b-dropdown
              variant="link"
              class="topbar-select-filter topbar-month-filter topbar-search-category-filter"
              toggle-class="topbar-select-filter-toggle dark-filter-control"
              menu-class="topbar-select-filter-menu dark-filter-menu"
              boundary="viewport"
              no-caret
            >
              <template #button-content>
                <span class="topbar-select-filter-text">{{ formatMonthLabel(monthSelected) }}</span>
                <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
              </template>
              <b-dropdown-item
                v-for="option in monthOptions"
                :key="`overflow-month-${option}`"
                link-class="topbar-select-filter-item"
                @click="$emit('update:monthSelected', option)"
              >
                {{ formatMonthLabel(option) }}
              </b-dropdown-item>
            </b-dropdown>
            <b-dropdown
              variant="link"
              class="topbar-select-filter topbar-year-filter topbar-search-category-filter"
              toggle-class="topbar-select-filter-toggle dark-filter-control"
              menu-class="topbar-select-filter-menu dark-filter-menu"
              boundary="viewport"
              no-caret
            >
              <template #button-content>
                <span class="topbar-select-filter-text">{{ yearSelected }}</span>
                <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
              </template>
              <b-dropdown-item
                v-for="option in yearOptions"
                :key="`overflow-year-${option}`"
                link-class="topbar-select-filter-item"
                @click="$emit('update:yearSelected', option)"
              >
                {{ option }}
              </b-dropdown-item>
            </b-dropdown>
            <button
              v-if="showClearButton"
              type="button"
              class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
              @click="$emit('clear-dates')"
            >
              Clear
            </button>
            <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
              Apply Filter
            </button>
          </div>
        </div>

        <template v-for="panelId in overflowPanelItemIds">
          <div
            v-if="panelId === 'select-filter'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Filter</span>
            <b-dropdown
              variant="link"
              class="topbar-select-filter"
              toggle-class="topbar-select-filter-toggle dark-filter-control"
              menu-class="topbar-select-filter-menu dark-filter-menu"
              boundary="viewport"
              no-caret
            >
              <template #button-content>
                <span class="topbar-select-filter-text">{{ selectedFilterLabel }}</span>
                <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
              </template>
              <b-dropdown-item
                v-for="option in filterOptions"
                :key="`overflow-filter-${option.text}-${option.value}`"
                link-class="topbar-select-filter-item"
                @click="$emit('update:filterSelected', option.value)"
              >
                {{ option.text }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div
            v-else-if="panelId === 'search-category'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">{{ searchCategoryFallbackLabel }}</span>
            <b-dropdown
              variant="link"
              class="topbar-select-filter"
              toggle-class="topbar-select-filter-toggle dark-filter-control"
              menu-class="topbar-select-filter-menu dark-filter-menu"
              boundary="viewport"
              no-caret
            >
              <template #button-content>
                <span class="topbar-select-filter-text">{{ searchCategorySelected || searchCategoryFallbackLabel }}</span>
                <img :src="chevronDownIcon" alt="" class="topbar-select-filter-chevron dark-filter-control-icon" />
              </template>
              <b-dropdown-item
                v-for="option in availableSearchCategoryOptions"
                :key="`overflow-category-${option}`"
                :link-class="[
                  'topbar-select-filter-item',
                  { 'topbar-select-filter-item--active': option === (searchCategorySelected || searchCategoryFallbackLabel) }
                ]"
                @click="$emit('update:searchCategorySelected', option)"
              >
                {{ option }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div
            v-else-if="panelId === 'dashboard-primary'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Region</span>
            <select
              class="form-select topbar-dashboard-select dark-filter-control topbar-dashboard-select--panel"
              :value="dashboardPrimarySelected"
              @change="$emit('update:dashboardPrimarySelected', $event.target.value)"
            >
              <option v-for="opt in dashboardPrimaryOptions" :key="`overflow-primary-${String(opt.value)}`" :value="opt.value">
                {{ opt.text }}
              </option>
            </select>
          </div>
          <div
            v-else-if="panelId === 'dashboard-secondary'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Country</span>
            <select
              class="form-select topbar-dashboard-select dark-filter-control topbar-dashboard-select--panel"
              :value="dashboardSecondarySelected"
              @change="$emit('update:dashboardSecondarySelected', $event.target.value)"
            >
              <option v-for="opt in dashboardSecondaryOptions" :key="`overflow-secondary-${String(opt.value)}`" :value="opt.value">
                {{ opt.text }}
              </option>
            </select>
          </div>
          <div
            v-else-if="panelId === 'apply-filter'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Apply</span>
            <button type="button" class="admin-topbar-btn admin-topbar-btn-primary" @click="$emit('apply-filter')">
              Apply Filter
            </button>
          </div>
          <div
            v-else-if="panelId === 'clear-button'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Clear</span>
            <button
              type="button"
              class="admin-topbar-btn admin-topbar-btn-clear dark-filter-control"
              @click="$emit('clear-dates')"
            >
              Clear
            </button>
          </div>
          <div
            v-else-if="panelId === 'end-date'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">{{ endDateFallbackLabel }}</span>
            <div
              class="topbar-date-field topbar-date-field--end dark-filter-control"
              :class="{ 'topbar-date-field--empty': !endDate, 'topbar-date-field--filled': !!endDate }"
            >
              <span class="topbar-date-text">{{ endDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="endDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:endDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-else-if="panelId === 'start-date'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">{{ startDateFallbackLabel }}</span>
            <div
              class="topbar-date-field topbar-date-field--start dark-filter-control"
              :class="{ 'topbar-date-field--empty': !startDate, 'topbar-date-field--filled': !!startDate }"
            >
              <span class="topbar-date-text">{{ startDateLabel }}</span>
              <b-form-datepicker
                button-only
                :class="['topbar-date-picker', { 'dark-calendar-control': isDarkMode }]"
                :menu-class="datePickerMenuClass"
                boundary="viewport"
                :popper-opts="datePickerPopperOpts"
                :value="startDate"
                :min="minDate"
                :max="maxDate"
                :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                @input="$emit('update:startDate', $event)"
              >
                <template #button-content>
                  <span class="topbar-calendar-trigger">
                    <font-awesome-icon :icon="['far', 'calendar']" class="topbar-calendar-icon dark-filter-control-icon" />
                  </span>
                </template>
              </b-form-datepicker>
            </div>
          </div>
          <div
            v-else-if="panelId === 'dark-mode'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Theme</span>
            <button
              type="button"
              class="admin-topbar-icon admin-topbar-dark-toggle"
              :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
              @click="$emit('toggle-dark')"
            >
              <img
                :src="isDarkMode ? sunOutlineIcon : moonIcon"
                alt=""
                class="topbar-action-icon topbar-action-icon-theme"
              />
            </button>
          </div>
          <div
            v-else-if="panelId === 'help' && !isQmsOtpGuestSession && !isQmsPayrollClosureRole"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Help</span>
            <router-link
              to="/contactUs"
              class="admin-topbar-icon admin-topbar-help-link"
              title="Help"
            >
              <font-awesome-icon :icon="['far', 'envelope']" class="topbar-action-icon topbar-action-icon-mail" />
              <span class="topbar-help-text">Help</span>
            </router-link>
          </div>
          <div
            v-else-if="panelId === 'notifications'"
            :key="`overflow-panel-${panelId}`"
            class="topbar-overflow-panel-row topbar-overflow-panel-row--utility"
          >
            <span class="topbar-overflow-panel-label">Notifications</span>
            <button
              type="button"
              class="admin-topbar-icon admin-topbar-icon-bell"
              title="Notifications"
              :aria-expanded="notificationPanelOpen"
              @click="toggleNotificationPanel"
            >
              <font-awesome-icon :icon="['far', 'bell']" class="topbar-action-icon topbar-action-icon-bell" />
              <span v-if="displayNotificationCount" class="topbar-notify-badge">{{ displayNotificationCount }}</span>
            </button>
          </div>
        </template>
      </topbar-overflow-menu>

    <notification-panel
      :visible="notificationPanelOpen"
      :notifications="panelNotifications"
      :loading="notificationLoading"
      :is-dark-mode="isDarkMode"
      @close="closeNotificationPanel"
    />
  </header>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import chevronDownIcon from '@/assets/images/chevron-down.png';
import searchIcon from '@/assets/images/search-icon.png';
import moonIcon from '@/assets/images/moon.png';
import sunOutlineIcon from '@/assets/images/sun-outline.svg';
import NotificationPanel from '@/components/NotificationPanel.vue';
import TopbarOverflowMenu from '@/components/TopbarOverflowMenu.vue';
import topbarOverflowMixin from '@/mixins/topbarOverflowMixin';
import { sortOverflowPanelIds } from '@/utils/topbarOverflow';

export default {
  name: 'AdminTopbar',
  components: { NotificationPanel, TopbarOverflowMenu },
  mixins: [topbarOverflowMixin],
  props: {
    search: { type: String, default: '' },
    searchPlaceholder: { type: String, default: 'Search...' },
    showSearch: { type: Boolean, default: true },
    showDashboardGeoFilters: Boolean,
    dashboardPrimaryOptions: {
      type: Array,
      default: () => []
    },
    dashboardSecondaryOptions: {
      type: Array,
      default: () => []
    },
    dashboardPrimarySelected: {
      type: String,
      default: 'select'
    },
    dashboardSecondarySelected: {
      type: String,
      default: 'select'
    },
    showDateFilters: Boolean,
    showClearButton: Boolean,
    showSelectFilter: Boolean,
    showSearchCategory: Boolean,
    searchCategoryOptions: {
      type: Array,
      default: () => []
    },
    searchCategorySelected: {
      type: String,
      default: ''
    },
    searchCategoryFallbackLabel: {
      type: String,
      default: 'CheckList Name'
    },
    filterOptions: {
      type: Array,
      default: () => []
    },
    filterSelected: {
      default: null
    },
    filterFallbackLabel: {
      type: String,
      default: 'Select Filter'
    },
    startDate: String,
    endDate: String,
    startDateFallbackLabel: {
      type: String,
      default: 'Start Date'
    },
    endDateFallbackLabel: {
      type: String,
      default: 'End Date'
    },
    minDate: String,
    maxDate: String,
    dateFiltersPlacement: {
      type: String,
      default: 'auto'
    },
    reverseDateOrder: {
      type: Boolean,
      default: false
    },
    singleDateFilter: {
      type: Boolean,
      default: false
    },
    notificationCount: { type: Number, default: null },
    isDarkMode: Boolean,
    showMonthYearFilters: Boolean,
    monthOptions: {
      type: Array,
      default: () => []
    },
    yearOptions: {
      type: Array,
      default: () => []
    },
    monthSelected: {
      type: String,
      default: ''
    },
    yearSelected: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      chevronDownIcon,
      searchIcon,
      moonIcon,
      sunOutlineIcon,
      notificationPanelOpen: false
    };
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'isQmsPayrollClosureRole']),
    ...mapState('notification', {
      storeNotifications: 'notifications',
      storeNotificationCount: 'notificationCount',
      notificationLoading: 'loading'
    }),
    availableSearchCategoryOptions() {
      // Keep every option visible in the open list (including the current selection),
      // matching how checklist/search filters show values in the dropdown panel.
      return Array.isArray(this.searchCategoryOptions) ? this.searchCategoryOptions : [];
    },
    isQmsOtpGuestSession() {
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      return sessionStorage.getItem('qmsOtpVerified') === 'true' && !this.isLoggedIn();
    },
    endDateLabel() {
      return this.formatDateLabel(this.endDate, this.endDateFallbackLabel);
    },
    startDateLabel() {
      return this.formatDateLabel(this.startDate, this.startDateFallbackLabel);
    },
    selectedFilterLabel() {
      const selected = (this.filterOptions || []).find(option => option.value === this.filterSelected);
      return selected ? selected.text : this.filterFallbackLabel;
    },
    // Events/NPS Vendor use select-filter as the search-key picker — keep it inside the search combo.
    // Pages that already have search-category (e.g. QMS status) keep select-filter outside.
    showSelectFilterInSearchCombo() {
      return this.showSearch && this.showSelectFilter && !this.showSearchCategory;
    },
    useUnifiedFilterBar() {
      return false;
    },
    useSplitFilterBar() {
      return (
        this.showMonthYearFilters ||
        (this.showDateFilters &&
          (this.showSearch || this.showSearchCategory || this.showSelectFilter) &&
          !this.showDashboardGeoFilters &&
          !this.showDateFiltersInLeading)
      );
    },
    hasLeadingContent() {
      return (
        this.showDashboardGeoFilters ||
        this.showSearch ||
        this.showSearchCategory ||
        this.showSelectFilter ||
        this.showDateFiltersInLeading
      );
    },
    showDateFiltersInLeading() {
      if (this.dateFiltersPlacement === 'actions') {
        return false;
      }
      return (
        this.showDateFilters &&
        !this.useUnifiedFilterBar &&
        !this.showDashboardGeoFilters &&
        !this.showSearch &&
        !this.showSelectFilter &&
        !this.showSearchCategory
      );
    },
    panelNotifications() {
      return this.storeNotifications;
    },
    displayNotificationCount() {
      if (this.notificationCount != null) {
        return this.notificationCount;
      }
      return this.storeNotificationCount;
    },
    datePickerMenuClass() {
      return this.isDarkMode ? 'dark-calendar-menu' : null;
    },
    datePickerPopperOpts() {
      return { positionFixed: true };
    },
    overflowPanelItemIds() {
      const itemIds = [
        'select-filter',
        'search-category',
        'dashboard-primary',
        'dashboard-secondary',
        'apply-filter',
        'clear-button',
        'end-date',
        'start-date',
        'dark-mode',
        'help',
        'notifications'
      ];
      return sortOverflowPanelIds(this.overflowedIds.filter(id => itemIds.includes(id)));
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onNotificationPanelKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onNotificationPanelKeydown);
  },
  methods: {
    ...mapActions('notification', ['list']),
    toggleNotificationPanel() {
      const opening = !this.notificationPanelOpen;
      this.notificationPanelOpen = opening;
      if (opening && this.isLoggedIn()) {
        this.list();
      }
    },
    closeNotificationPanel() {
      this.notificationPanelOpen = false;
    },
    onNotificationPanelKeydown(event) {
      if (event.key === 'Escape' && this.notificationPanelOpen) {
        this.closeNotificationPanel();
      }
    },
    formatMonthLabel(value) {
      if (!value) {
        return 'Month';
      }
      const normalized = String(value).toLowerCase();
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    },
    formatDateLabel(value, fallbackLabel) {
      if (!value) {
        return fallbackLabel;
      }

      const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }
};
</script>

<style scoped>
.admin-topbar {
  position: sticky;
  top: 0;
  z-index: 1020;
  min-height: 64px;
  padding-top: 12px;
  padding-bottom: 12px;
  box-sizing: border-box;
  background: transparent !important;
  border-bottom: 0 !important;
  box-shadow: none;
}

.admin-topbar--responsive {
  flex-wrap: nowrap !important;
  overflow: visible;
}

.admin-topbar-overflow-slot {
  flex: 0 0 auto;
}

.admin-topbar--responsive .admin-topbar-leading {
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}

.admin-topbar--responsive.admin-topbar-with-split-filters .admin-topbar-leading,
.admin-topbar--responsive.admin-topbar-with-full-filters .admin-topbar-leading,
.admin-topbar--responsive.admin-topbar-with-context-filter .admin-topbar-leading,
.admin-topbar--responsive.admin-topbar-with-search-category .admin-topbar-leading {
  flex: 0 1 auto;
  min-width: auto;
  overflow: visible;
}

.admin-topbar--responsive.admin-topbar-with-search-category .admin-topbar-actions {
  margin-left: auto;
}

.admin-topbar-with-dashboard-geo .admin-topbar-leading,
.admin-topbar--responsive.admin-topbar-with-dashboard-geo .admin-topbar-leading {
  flex: 0 1 auto;
  min-width: auto;
  overflow: visible;
}

.admin-topbar--responsive .topbar-dashboard-geo-filters {
  flex: 0 0 auto;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

.admin-topbar-spacer {
  flex: 1 1 auto;
  min-width: 0;
}

.admin-topbar--responsive .admin-topbar-actions {
  flex: 0 0 auto;
  flex-wrap: nowrap !important;
  width: auto !important;
  justify-content: flex-end;
}

.admin-topbar--responsive .admin-topbar-actions.ms-auto {
  margin-left: auto;
}

.topbar-overflow-item {
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .admin-topbar {
    height: 64px;
  }
}

.admin-topbar-leading {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;
}

.admin-topbar-with-date-filters .admin-topbar-leading {
  flex: 1 1 auto;
  min-width: 0;
}

.admin-topbar-with-dashboard-geo {
  flex-wrap: nowrap;
  gap: 12px;
}

.admin-topbar-with-dashboard-geo .admin-topbar-leading {
  flex: 0 1 auto;
  min-width: auto;
  gap: 12px;
  overflow: visible;
}

.admin-topbar-with-dashboard-geo .topbar-dashboard-geo-filters {
  flex: 0 0 auto;
  flex-shrink: 0;
}

.admin-topbar-with-dashboard-geo .admin-topbar-actions {
  flex: 0 0 auto;
  margin-left: auto;
  gap: 12px;
  flex-wrap: nowrap;
}

.topbar-dashboard-geo-filters {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  flex-shrink: 0;
  flex-wrap: nowrap;
  gap: 12px;

  --topbar-dashboard-select-width: 220px;
}

.topbar-dashboard-geo-item {
  flex: 0 0 auto;
  flex-shrink: 0;
}

.topbar-dashboard-select {
  flex: 0 0 var(--topbar-dashboard-select-width, 220px);
  flex-shrink: 0;
  width: var(--topbar-dashboard-select-width, 220px);
  min-width: var(--topbar-dashboard-select-width, 220px);
  max-width: var(--topbar-dashboard-select-width, 220px);
  height: 40px;
  box-sizing: border-box;
  padding: 4px 36px 4px 12px;
  border: 1px solid #f1f1f1;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background:
    #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4 6L8 10L12 6' stroke='%23454545' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    no-repeat right 12px center;
  appearance: none;
  color: #19243b;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  box-shadow: none !important;
}

.topbar-dashboard-geo-filters .topbar-dashboard-select.form-select {
  flex: 0 0 var(--topbar-dashboard-select-width, 220px);
  flex-shrink: 0;
  width: var(--topbar-dashboard-select-width, 220px);
  min-width: var(--topbar-dashboard-select-width, 220px);
  max-width: var(--topbar-dashboard-select-width, 220px);
}

.topbar-dashboard-select:focus {
  border-color: #c5d4e8;
  outline: none;
  box-shadow: none;
}

.admin-topbar-with-leading-date-filters .admin-topbar-leading {
  flex: 1 1 auto;
  min-width: 0;
}

.admin-topbar-with-leading-date-filters .topbar-filter-group-leading {
  flex-wrap: wrap;
}

.admin-topbar-with-date-filters .admin-topbar-search {
  flex: 1 1 auto;
  width: 100%;
  max-width: 640px;
}

.admin-topbar-with-date-filters .admin-topbar-actions {
  flex: 0 0 auto;
  margin-left: auto;
  gap: 12px;
  flex-wrap: nowrap;
}

/* Reports / Events / Food Safety: search + select filter + dates in one row */
.admin-topbar-unified-filters {
  flex-wrap: nowrap;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.admin-topbar-leading-unified {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
}

.admin-topbar-leading-unified .topbar-search-combo {
  flex: 1 1 280px;
  width: auto;
  min-width: 200px;
  max-width: 420px;
}

.admin-topbar-leading-unified .admin-topbar-search {
  flex: 1 1 240px;
  width: auto;
  min-width: 160px;
  max-width: 320px;
}

.admin-topbar-leading-unified .topbar-context-filter {
  flex: 0 0 auto;
}

.admin-topbar-leading-unified .topbar-filter-group {
  flex: 0 0 auto;
}

.admin-topbar-leading-unified .topbar-utility-icons-unified {
  flex: 0 0 auto;
  margin-left: auto;
}

.admin-topbar-leading-unified .topbar-date-field {
  width: 136px;
  min-width: 136px;
}

.admin-topbar-leading-unified .topbar-select-filter-text {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-topbar-with-full-filters {
  flex-wrap: nowrap;
  gap: 12px;
}

.admin-topbar-with-full-filters .admin-topbar-leading {
  flex: 1 1 auto;
  min-width: 0;
  gap: 12px;
}

.admin-topbar-with-full-filters .topbar-search-combo {
  flex: 1 1 auto;
  width: auto;
  max-width: 560px;
  min-width: 220px;
}

.admin-topbar-with-full-filters .admin-topbar-search {
  flex: 1 1 auto;
  width: auto;
  max-width: 480px;
  min-width: 180px;
}

.admin-topbar-with-full-filters .topbar-context-filter {
  flex: 0 0 auto;
  gap: 12px;
}

.admin-topbar-with-full-filters .admin-topbar-actions {
  flex: 0 0 auto;
  margin-left: auto;
  gap: 12px;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

@media (min-width: 768px) {
  .admin-topbar-with-full-filters .topbar-context-filter {
    position: relative;
    left: auto;
    transform: none;
  }
}

.admin-topbar-with-full-filters .topbar-search-category-filter {
  flex: 0 0 auto;
}

.admin-topbar-with-full-filters .topbar-context-filter + .topbar-context-filter {
  margin-left: 0;
}

.topbar-filter-group {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.topbar-filter-group .topbar-overflow-item {
  flex-shrink: 0;
}

.topbar-filter-group-reverse-date .topbar-date-field--end {
  order: 1;
}

.topbar-filter-group-reverse-date .topbar-date-field--start {
  order: 2;
}

.topbar-filter-group-reverse-date .admin-topbar-btn {
  order: 3;
}

.topbar-utility-icons {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.topbar-utility-icons .topbar-overflow-item {
  flex-shrink: 0;
}

.topbar-vertical-divider-actions,
.topbar-vertical-divider-utility {
  align-self: center;
}

.admin-topbar-with-context-filter .admin-topbar-leading {
  flex: 1 1 0;
}

@media (min-width: 768px) {
  .admin-topbar-with-context-filter {
    position: relative;
  }

  .admin-topbar-with-context-filter .topbar-context-filter {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
  }
}

.admin-topbar-with-context-filter .admin-topbar-actions {
  flex: 1 1 0;
  justify-content: flex-end;
}

.topbar-context-filter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
}

.topbar-vertical-divider {
  width: 1px;
  height: 24px;
  flex-shrink: 0;
  background: #e8edf4;
}

.topbar-vertical-divider-leading {
  flex-shrink: 0;
}

.topbar-search-combo {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  flex: 1 1 480px;
  max-width: 560px;
  min-width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #e8edf4;
  border-radius: 8px;
  background: #fff;
  overflow: visible;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.topbar-search-combo:hover {
  border-color: #c5d4e8;
}

.topbar-search-combo:focus-within {
  border-color: #2d64b8;
  box-shadow: 0 0 0 3px rgba(45, 100, 184, 0.12);
}

.topbar-search-combo .admin-topbar-search {
  flex: 1 1 auto;
  max-width: none;
  min-width: 140px;
  height: 100%;
  margin: 0;
  padding: 0 10px 0 12px;
  border: 0 !important;
  border-radius: 0;
  background: transparent !important;
  box-shadow: none !important;
}

.topbar-search-combo .admin-topbar-search:hover,
.topbar-search-combo .admin-topbar-search:focus-within {
  border: 0 !important;
  box-shadow: none !important;
}

.topbar-search-combo .topbar-search-category-filter {
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  height: 100%;
  margin: 0;
  padding: 0 10px 0 12px;
  border: 0 !important;
  border-left: 1px solid #e8edf4 !important;
  border-radius: 0;
  background: transparent !important;
  box-shadow: none !important;
}

.topbar-search-combo .topbar-search-category-filter:hover,
.topbar-search-combo .topbar-search-category-filter:focus-within {
  border-left-color: #e8edf4 !important;
  box-shadow: none !important;
}

.topbar-search-combo .topbar-select-filter :deep(.dropdown-menu),
.topbar-search-combo .topbar-select-filter :deep(.topbar-select-filter-menu) {
  z-index: 1080 !important;
  min-width: 160px;
  margin-top: 6px;
  border-radius: 8px;
}

.admin-topbar-search {
  position: relative;
  flex: 1 1 480px;
  max-width: 480px;
  min-width: 180px;
  margin: 0;
  box-sizing: border-box;
  height: 40px;
  display: inline-flex;
  align-items: center;
  padding: 0 10px 0 12px;
  border: 1px solid #e8edf4;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.admin-topbar-search:hover {
  border-color: #c5d4e8;
}

.admin-topbar-search:focus-within {
  border-color: #2d64b8;
  box-shadow: 0 0 0 3px rgba(45, 100, 184, 0.12);
}

.admin-topbar-search--pinned {
  flex-shrink: 1;
  min-width: 160px;
}

.topbar-search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  pointer-events: none;
}

.topbar-search-icon-img {
  position: absolute;
  top: 2.5px;
  left: 2.5px;
  width: 15px;
  height: 15px;
  opacity: 1;
  object-fit: contain;
  display: block;
}

.admin-topbar-search .form-control {
  height: 38px;
  padding-left: 28px;
  padding-right: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #19243b;
  -webkit-text-fill-color: #19243b;
  caret-color: #19243b;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  box-shadow: none;
}

.admin-topbar-search .form-control:focus {
  border: 0;
  background: transparent;
  box-shadow: none;
  outline: none;
}

.admin-topbar-search .form-control::placeholder {
  color: #c5c5c5;
  -webkit-text-fill-color: #c5c5c5;
  opacity: 1;
}

.admin-topbar--dark .admin-topbar-search {
  border-color: #3d4d66;
  background: #172033;
}

.admin-topbar--dark .admin-topbar-search:hover {
  border-color: #5a6f8c;
}

.admin-topbar--dark .admin-topbar-search:focus-within {
  border-color: #7eb8ff;
  box-shadow: 0 0 0 3px rgba(126, 184, 255, 0.16);
}

.admin-topbar--dark .admin-topbar-search .form-control {
  color: #f5f7fb;
  -webkit-text-fill-color: #f5f7fb;
  caret-color: #f5f7fb;
}

.admin-topbar--dark .admin-topbar-search .form-control::placeholder {
  color: #c5c5c5;
  -webkit-text-fill-color: #c5c5c5;
  opacity: 1;
}

.topbar-search-category-filter {
  box-sizing: border-box;
  height: 40px;
  padding: 0 10px 0 12px;
  border: 1px solid #e8edf4;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.topbar-search-category-filter:hover {
  border-color: #c5d4e8;
}

.topbar-search-category-filter:focus-within {
  border-color: #2d64b8;
  box-shadow: 0 0 0 3px rgba(45, 100, 184, 0.12);
}

.admin-topbar--dark .topbar-search-category-filter {
  border-color: #3d4d66;
  background: #172033;
}

.admin-topbar--dark .topbar-search-category-filter:hover {
  border-color: #5a6f8c;
}

.admin-topbar--dark .topbar-search-category-filter:focus-within {
  border-color: #7eb8ff;
  box-shadow: 0 0 0 3px rgba(126, 184, 255, 0.16);
}

.topbar-date-field {
  box-sizing: border-box;
  width: 148px;
  min-width: 148px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 0 10px 0 12px;
  border: 1px solid #e8edf4;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.topbar-date-field:hover {
  border-color: #c5d4e8;
}

.topbar-date-field:focus-within {
  border-color: #2d64b8;
  box-shadow: 0 0 0 3px rgba(45, 100, 184, 0.12);
}

.topbar-date-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.topbar-date-field--empty .topbar-date-text {
  color: #c5c5c5;
}

.topbar-date-field--filled .topbar-date-text {
  color: #19243b;
  font-weight: 500;
}

.topbar-date-picker {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
}

.topbar-calendar-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #6d788a;
  background: transparent;
  transition: color 0.15s ease;
}

.topbar-date-field:hover .topbar-calendar-trigger,
.topbar-date-field:focus-within .topbar-calendar-trigger {
  background: transparent;
  color: #2d64b8;
}

.topbar-calendar-icon {
  width: 16px;
  height: 16px;
  font-size: 14px;
  line-height: 1;
  display: block;
  color: inherit;
  opacity: 1;
}

.topbar-date-field--filled .topbar-calendar-icon {
  opacity: 1;
}

.admin-topbar-btn {
  box-sizing: border-box;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
  white-space: nowrap;
  cursor: pointer;
}

.admin-topbar-btn-clear {
  min-width: 72px;
  padding: 8px 18px;
  border: 1px solid #2d64b8;
  background: #fff;
  color: #2d64b8;
  box-shadow: none;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.admin-topbar-btn-clear:hover,
.admin-topbar-btn-clear:focus {
  border-color: #245299;
  background: #eef4fc;
  color: #245299;
  box-shadow: none;
}

.admin-topbar-btn-primary {
  width: 99px;
  min-width: 99px;
  background: #2d64b8;
  color: #fff;
  box-shadow: 0 4px 12px 0 rgba(75, 134, 223, 0.3);
}

.admin-topbar-btn-primary:hover,
.admin-topbar-btn-primary:focus {
  background: #245299;
  color: #fff;
  box-shadow: 0 4px 12px 0 rgba(75, 134, 223, 0.3);
}

.admin-topbar-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6d788a;
  cursor: pointer;
}

.admin-topbar-dark-toggle {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  line-height: 0;
}

.admin-topbar-menu-btn {
  width: 38px;
  height: 38px;
  border: 1px solid #f1f1f1;
  border-radius: 8px;
  background: #fff;
}

.topbar-action-icon {
  display: block;
  object-fit: contain;
  color: inherit;
  line-height: 1;
}

.topbar-action-icon-mail {
  width: 18px;
  height: 18px;
  font-size: 16px;
}

.topbar-action-icon-bell {
  width: 18px;
  height: 18px;
  font-size: 16px;
}

.topbar-action-icon-theme {
  width: 24px;
  height: 24px;
}

.admin-topbar-icon-bell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  width: auto;
  height: auto;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.admin-topbar-icon-bell:hover,
.admin-topbar-icon-bell:focus,
.admin-topbar-icon-bell:focus-visible,
.admin-topbar-icon-bell:active {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  outline: none;
  color: #2d64b8;
}

.admin-topbar-help-link {
  gap: 6px;
  flex-shrink: 0;
  width: auto;
  height: auto;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  text-decoration: none;
  color: #6d788a;
}

.admin-topbar-help-link:hover,
.admin-topbar-help-link:focus,
.admin-topbar-help-link:focus-visible,
.admin-topbar-help-link:active {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  outline: none;
  color: #2d64b8;
  text-decoration: none;
}

.topbar-help-text {
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
}

.topbar-notify-badge {
  box-sizing: border-box;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 2px;
  background: #d8484e;
  color: #fff;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0;
  text-align: center;
}

.topbar-select-filter {
  flex-shrink: 0;
}

.topbar-select-filter :deep(.topbar-select-filter-toggle) {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: auto;
  height: 40px;
  padding: 4px 8px 4px 0;
  border: 0;
  border-radius: 0;
  background: transparent !important;
  box-shadow: none !important;
  text-decoration: none !important;
}

.topbar-select-filter :deep(.topbar-select-filter-toggle:hover),
.topbar-select-filter :deep(.topbar-select-filter-toggle:focus) {
  border: 0;
  box-shadow: none !important;
  outline: none;
}

.topbar-select-filter-text {
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0;
  text-align: center;
  opacity: 1;
  visibility: visible;
  white-space: nowrap;
}

.topbar-select-filter-chevron {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.topbar-select-filter :deep(.topbar-select-filter-menu) {
  min-width: 160px;
  padding: 6px 0;
  border-radius: 8px;
  z-index: 1080;
}

.topbar-select-filter :deep(.topbar-select-filter-item) {
  padding: 8px 12px;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  opacity: 1;
  visibility: visible;
}

.admin-topbar-with-split-filters {
  flex-wrap: nowrap;
  gap: 12px;
}

.admin-topbar-with-split-filters .admin-topbar-leading {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
}

.admin-topbar-with-split-filters .admin-topbar-search {
  flex: 1 1 auto;
  width: auto;
  min-width: 160px;
  max-width: 320px;
}

.admin-topbar-with-split-filters .topbar-context-filter {
  flex: 0 0 auto;
  position: relative;
  left: auto;
  transform: none;
}

.admin-topbar-with-split-filters .admin-topbar-actions {
  flex: 0 0 auto;
  margin-left: auto;
  flex-wrap: nowrap;
}

.admin-topbar-with-split-filters .topbar-month-year-group,
.admin-topbar-with-split-filters .topbar-filter-group {
  flex-wrap: nowrap;
}

.admin-topbar-with-split-filters .topbar-month-filter,
.admin-topbar-with-split-filters .topbar-year-filter {
  flex: 0 0 auto;
}

.admin-topbar-with-split-filters .topbar-select-filter-text {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-topbar-actions {
  flex-shrink: 0;
}

.admin-topbar-actions:not(.ms-auto) {
  gap: 12px;
}

@media (max-width: 767.98px) {
  /* Compact Help to icon-only so utilities fit more often on tablet/mobile */
  .topbar-utility-icons .topbar-help-text,
  .topbar-utility-icons-unified .topbar-help-text {
    display: none;
  }

  .admin-topbar--responsive.admin-topbar-unified-filters {
    flex-wrap: nowrap !important;
  }

  .admin-topbar--responsive .admin-topbar-leading-unified {
    flex: 1 1 auto;
    flex-wrap: nowrap;
    min-width: 0;
  }

  .admin-topbar--responsive .admin-topbar-leading-unified .topbar-utility-icons-unified {
    margin-left: 0;
    width: auto;
    justify-content: flex-end;
  }

  .admin-topbar-unified-filters {
    flex-wrap: wrap;
  }

  .admin-topbar-leading-unified {
    flex: 1 1 100%;
    flex-wrap: wrap;
  }

  .admin-topbar-leading-unified .topbar-utility-icons-unified {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }

  .admin-topbar-unified-filters .admin-topbar-menu-btn {
    order: 0;
  }

  .admin-topbar--responsive.admin-topbar-with-dashboard-geo {
    flex-wrap: nowrap !important;
  }

  .admin-topbar-with-dashboard-geo {
    flex-wrap: wrap;
  }

  .admin-topbar--responsive .admin-topbar-with-dashboard-geo .topbar-dashboard-select,
  .admin-topbar-with-dashboard-geo .topbar-dashboard-select {
    flex: 0 0 var(--topbar-dashboard-select-width, 220px);
    flex-shrink: 0;
    width: var(--topbar-dashboard-select-width, 220px);
    min-width: var(--topbar-dashboard-select-width, 220px);
    max-width: var(--topbar-dashboard-select-width, 220px);
    box-sizing: border-box;
  }

  .admin-topbar--responsive.admin-topbar-with-full-filters {
    flex-wrap: nowrap !important;
  }

  .admin-topbar-with-full-filters {
    flex-wrap: wrap;
  }

  .admin-topbar--responsive .admin-topbar-with-full-filters .admin-topbar-leading {
    flex: 0 1 auto;
    min-width: auto;
  }

  .admin-topbar-with-full-filters .admin-topbar-leading {
    flex: 1 1 100%;
  }

  .admin-topbar--responsive .admin-topbar-with-full-filters .topbar-context-filter {
    flex: 0 0 auto;
  }

  .admin-topbar-with-full-filters .topbar-context-filter {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .admin-topbar--responsive .admin-topbar-with-full-filters .admin-topbar-search {
    max-width: none;
    min-width: 160px;
    flex: 1 1 auto;
  }

  .admin-topbar-with-full-filters .admin-topbar-search {
    max-width: none;
  }

  .admin-topbar--responsive .admin-topbar-with-date-filters .admin-topbar-actions {
    flex-wrap: nowrap !important;
    width: auto !important;
  }

  .admin-topbar-with-date-filters .admin-topbar-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .admin-topbar--responsive .topbar-filter-group {
    flex-wrap: nowrap;
  }

  .topbar-filter-group {
    flex-wrap: wrap;
  }

  .topbar-vertical-divider-leading {
    display: none !important;
  }

  .topbar-vertical-divider-actions {
    display: none !important;
  }

  .admin-topbar--responsive .admin-topbar-leading {
    flex: 1 1 auto;
    min-width: 0;
  }

  .admin-topbar-leading {
    flex: 1 1 100%;
  }

  .admin-topbar--responsive .admin-topbar-with-context-filter .admin-topbar-leading,
  .admin-topbar--responsive .admin-topbar-with-context-filter .admin-topbar-actions {
    flex: 0 1 auto;
  }

  .admin-topbar--responsive .admin-topbar-with-context-filter .admin-topbar-leading {
    min-width: auto;
    overflow: visible;
  }

  .admin-topbar-with-context-filter .admin-topbar-leading,
  .admin-topbar-with-context-filter .admin-topbar-actions {
    flex: 1 1 auto;
  }

  .admin-topbar--responsive .topbar-context-filter {
    flex: 0 0 auto;
  }

  .topbar-context-filter {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .admin-topbar--responsive .admin-topbar-search {
    flex: 1 1 auto;
    min-width: 160px;
    max-width: none;
  }

  .admin-topbar--responsive .admin-topbar-search--pinned {
    flex-shrink: 1;
  }

  .admin-topbar-search {
    flex: 1 1 auto;
    max-width: none;
    min-width: 160px;
  }

  .admin-topbar--responsive.admin-topbar-with-split-filters {
    flex-wrap: nowrap !important;
  }

  .admin-topbar-with-split-filters {
    flex-wrap: wrap;
  }

  .admin-topbar--responsive .admin-topbar-with-split-filters .admin-topbar-leading {
    flex: 0 1 auto;
    min-width: auto;
    flex-wrap: nowrap;
    overflow: visible;
  }

  .admin-topbar--responsive .admin-topbar-with-full-filters .admin-topbar-leading {
    flex: 0 1 auto;
    min-width: auto;
    overflow: visible;
  }

  .admin-topbar--responsive .admin-topbar-with-context-filter .admin-topbar-leading {
    flex: 0 1 auto;
    min-width: auto;
    overflow: visible;
  }

  .admin-topbar--responsive .admin-topbar-with-split-filters .admin-topbar-search {
    flex: 1 1 auto;
    min-width: 160px;
    max-width: 320px;
  }

  .admin-topbar--responsive .admin-topbar-with-split-filters .topbar-context-filter {
    flex: 0 0 auto;
  }

  .admin-topbar--responsive .admin-topbar-with-split-filters .admin-topbar-actions {
    flex-wrap: nowrap !important;
    width: auto !important;
  }

  .admin-topbar--responsive .admin-topbar-actions {
    width: auto !important;
    justify-content: flex-end;
  }

  .admin-topbar-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .admin-topbar--responsive .topbar-date-field {
    flex: 0 0 auto;
    width: 148px;
    min-width: 130px;
  }

  .topbar-date-field {
    flex: 1 1 calc(50% - 10px);
    width: auto;
    min-width: 130px;
  }

  .admin-topbar-btn-clear,
  .admin-topbar-btn-primary {
    width: auto;
    min-width: 72px;
  }
}
</style>

<style>
/* Bootstrap mounts dropdown menus outside the scoped component tree. */
.topbar-select-filter-menu,
.topbar-select-filter .dropdown-menu,
.dark-filter-menu {
  z-index: 1080 !important;
  min-width: 160px !important;
  padding: 6px 0 !important;
  border-radius: 8px !important;
}

.topbar-select-filter-menu .dropdown-item,
.topbar-select-filter-menu .topbar-select-filter-item,
.topbar-select-filter .dropdown-menu .dropdown-item,
.topbar-select-filter .dropdown-menu .topbar-select-filter-item,
.dark-filter-menu .dropdown-item {
  box-sizing: border-box;
  padding: 8px 12px !important;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif !important;
  font-size: 12px !important;
  font-weight: 400;
  line-height: 16px !important;
}

/* Only the calendar icon trigger — do not affect dropdown calendar day buttons */
.topbar-date-field .topbar-date-picker.b-form-btn-label-control > button.btn.dropdown-toggle {
  padding: 0;
  margin: 0;
  border: 0;
  background: transparent !important;
  box-shadow: none !important;
  min-width: 24px;
  width: 24px;
  height: 24px;
  line-height: 1;
  color: #6d788a !important;
}

.topbar-date-field .topbar-date-picker.b-form-btn-label-control > button.btn.dropdown-toggle:hover,
.topbar-date-field .topbar-date-picker.b-form-btn-label-control > button.btn.dropdown-toggle:focus,
.topbar-date-field .topbar-date-picker.b-form-btn-label-control > button.btn.dropdown-toggle:active {
  box-shadow: none !important;
  outline: none;
  background: transparent !important;
  color: #2d64b8 !important;
}

.topbar-date-field .topbar-date-picker.b-form-btn-label-control > button.btn.dropdown-toggle .topbar-calendar-icon {
  color: inherit !important;
}

.topbar-date-picker .dropdown-menu,
.dark-calendar-menu.dropdown-menu,
.dark-calendar-menu {
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}
</style>
